import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { CheckCircleOutlined } from "@ant-design/icons";
import { Tag, Tooltip, theme } from "antd";
import { observer } from "mobx-react";
import { EAffiliateTransactionTrackingMethod, EAffiliateTransactionType } from "@devowl-wp/api-real-commerce";
import { useStores } from "../../../../../../store/stores.js";
import { formatAmount } from "../../../../../../utils/formatAmount.js";
import { formatLocaleDate } from "../../../../../../utils/formatLocaleDate.js";
import { __, _i } from "../../../../../../utils/i18n.js";
import { MATOMO_MASK } from "../../../../../../utils/matomoMask.js";
// Invoice-style divided rows: product + plan on the left, labelled
// Status / Date / Source / Net commission columns on the right.
const TransactionsTable = observer(()=>{
    const { token } = theme.useToken();
    const { accountV2Store: { session } } = useStores();
    const affiliate = session?.affiliate;
    const rows = affiliate ? Array.from(affiliate.transactions.values()) : [];
    return /*#__PURE__*/ _jsx("div", {
        className: "rc-account-v2-affiliate-list",
        children: rows.map((row)=>{
            const isRefund = row.type === EAffiliateTransactionType.Refund;
            const sourceHost = row.linkView?.referer ? new URL(row.linkView.referer).hostname : undefined;
            return /*#__PURE__*/ _jsxs("div", {
                className: "rc-account-v2-affiliate-row",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-affiliate-row__info",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rc-account-v2-affiliate-row__name",
                                children: [
                                    row.product.name,
                                    isRefund && /*#__PURE__*/ _jsx(Tag, {
                                        color: "error",
                                        children: __("Refund")
                                    }),
                                    !!row.coupon && /*#__PURE__*/ _jsx(Tooltip, {
                                        title: _i(__('This transaction was assigned to you because the buyer used the coupon code "{{span}}%s{{/span}}".', row.coupon.code), {
                                            span: /*#__PURE__*/ _jsx("span", {
                                                ...MATOMO_MASK
                                            })
                                        }),
                                        children: /*#__PURE__*/ _jsx(Tag, {
                                            color: "blue",
                                            children: __("Coupon")
                                        })
                                    }),
                                    row.trackingMethod === EAffiliateTransactionTrackingMethod.Support && /*#__PURE__*/ _jsx(Tooltip, {
                                        title: __("This transaction was subsequently assigned by a supporter."),
                                        children: /*#__PURE__*/ _jsx(Tag, {
                                            color: "blue",
                                            children: __("Support")
                                        })
                                    })
                                ]
                            }),
                            !!row.productPlan.description && /*#__PURE__*/ _jsx("div", {
                                className: "rc-account-v2-affiliate-row__sub",
                                children: /*#__PURE__*/ _jsx("span", {
                                    children: row.productPlan.description
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "rc-account-v2-affiliate-row__meta",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rc-account-v2-affiliate-row__meta-col",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "rc-account-v2-affiliate-row__meta-label",
                                        children: __("Status")
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "rc-account-v2-affiliate-row__value",
                                        children: row.payout ? /*#__PURE__*/ _jsx(Tooltip, {
                                            title: _i(__("Received in payout {{id}}#%s{{/id}} at {{date}}%s{{/date}}", row.payout.id, formatLocaleDate(row.payout.createdAt)), {
                                                id: /*#__PURE__*/ _jsx("span", {
                                                    ...MATOMO_MASK
                                                }),
                                                date: /*#__PURE__*/ _jsx("span", {
                                                    ...MATOMO_MASK
                                                })
                                            }),
                                            children: /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    /*#__PURE__*/ _jsx(CheckCircleOutlined, {
                                                        style: {
                                                            color: token.colorSuccess
                                                        }
                                                    }),
                                                    " ",
                                                    __("Paid")
                                                ]
                                            })
                                        }) : /*#__PURE__*/ _jsx(Tooltip, {
                                            title: __("The transaction was tracked and accepted. Commission will be paid to you on the 15th of the month following the month in which you earned it, if you reach the payout threshold."),
                                            children: /*#__PURE__*/ _jsxs("span", {
                                                children: [
                                                    /*#__PURE__*/ _jsx(CheckCircleOutlined, {
                                                        style: {
                                                            color: token.colorPrimary
                                                        }
                                                    }),
                                                    " ",
                                                    __("Accepted")
                                                ]
                                            })
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rc-account-v2-affiliate-row__meta-col",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "rc-account-v2-affiliate-row__meta-label",
                                        children: __("Date")
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "rc-account-v2-affiliate-row__value",
                                        children: formatLocaleDate(row.createdAt)
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rc-account-v2-affiliate-row__meta-col",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "rc-account-v2-affiliate-row__meta-label",
                                        children: __("Source")
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "rc-account-v2-affiliate-row__value",
                                        children: sourceHost ? /*#__PURE__*/ _jsx("a", {
                                            href: row.linkView.referer,
                                            target: "_blank",
                                            rel: "noreferrer",
                                            ...MATOMO_MASK,
                                            children: sourceHost
                                        }) : /*#__PURE__*/ _jsx("i", {
                                            children: __("not specified")
                                        })
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "rc-account-v2-affiliate-row__meta-col",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "rc-account-v2-affiliate-row__meta-label",
                                        children: __("Net commission")
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "rc-account-v2-affiliate-row__amount",
                                        style: {
                                            color: isRefund ? token.colorError : token.colorSuccess
                                        },
                                        children: [
                                            isRefund ? "-" : "+",
                                            " ",
                                            formatAmount({
                                                amount: row.netCommissionValue,
                                                currency: "EUR"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "rc-account-v2-affiliate-row__amount-sub",
                                        children: [
                                            "/ ",
                                            formatAmount({
                                                amount: row.netTransactionValue,
                                                currency: "EUR"
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }, row.id);
        })
    });
});
export { TransactionsTable };
